import { isEqual } from 'lodash';
import { API_SHOOTINGS_V2_PATH, getKodakClient } from '../api';
import { Property, Shooting } from '../dtos';
import {
  PaginatedResult, PaginationParams, serializePagination, SortOrder,
} from '../../pagination';
import { ShootingStatus } from '../../../domain/types';
import { serializeParams } from '../utils/serializer';

export async function createShooting(shooting: Shooting): Promise<Shooting> {
  const client = await getKodakClient();
  const { data } = await client.post<Shooting>(API_SHOOTINGS_V2_PATH, shooting);
  return data;
}

const THANOS_MANAGED_FIELDS: ReadonlyArray<keyof Shooting> = [
  'type',
  'photographerId',
  'dueDate',
  'deliveryDate',
  'virtualTourType',
  'price',
  'notes',
  'projectStatus',
];

export async function updateShooting(shooting: Shooting, originalShooting: Shooting): Promise<Shooting> {
  const client = await getKodakClient();
  const { data: latestShooting } = await client.get<Shooting>(`${API_SHOOTINGS_V2_PATH}/${shooting.id}`);

  const changedFields = THANOS_MANAGED_FIELDS
    .filter((field) => !isEqual(shooting[field], originalShooting[field]));

  const changes = Object.fromEntries(changedFields.map((field) => [field, shooting[field] ?? null]));

  const { data } = await client.put<Shooting>(`${API_SHOOTINGS_V2_PATH}/${shooting.id}`, {
    ...latestShooting,
    ...changes,
    files: [],
  });
  return data;
}

export async function getShooting(shootingId: NonNullable<Shooting['id']>): Promise<Shooting> {
  const client = await getKodakClient();
  const { data } = await client.get<Shooting>(`${API_SHOOTINGS_V2_PATH}/${shootingId}`);
  return data;
}

export async function getLatestShootingByPropertyId(propertyId: NonNullable<Property['id']>): Promise<Shooting | undefined> {
  const client = await getKodakClient();
  const { data } = await client.get<PaginatedResult<Shooting>>(API_SHOOTINGS_V2_PATH, {
    params: {
      property_id: propertyId,
      ...serializePagination({ size: 1, sort: { createdAt: SortOrder.DESC } }),
    },
  });
  return data.content[0];
}

export interface ShootingsByPropertyIdFilters {
  status?: ShootingStatus[];
}

export async function getAllShootingsByPropertyId(
  propertyId: NonNullable<Property['id']>,
  filters?: ShootingsByPropertyIdFilters,
  pagination: PaginationParams = { size: 20, sort: { createdAt: SortOrder.DESC } },
): Promise<Shooting[] | undefined> {
  const client = await getKodakClient();
  const { data } = await client.get<PaginatedResult<Shooting>>(API_SHOOTINGS_V2_PATH, {
    params: {
      property_id: propertyId,
      ...serializeParams(filters || {}),
      ...serializePagination(pagination),
    },
  });
  return data.content;
}
